import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { updateShopOrder } from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import {
  ShopOrderForSeller,
  ShopOrderSellerRequestStatus,
} from '@shared/api/shopOrder/shopOrder.interface';
import {
  isDepositLikePayment,
  sellerRequestStatusLabel,
} from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import ConfirmModal from '@shared/components/ConfirmModal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { DeliveryState, PaymentState } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { deliveryStateToString } from '@shared/utils/stateUtils';
import { useQueryClient } from 'react-query';

import { DeliveredModal } from './DeliveredModal';
import { OrderManualRefundRequestModal } from './OrderManualRefundRequestModal';
import { OrderTaxInvoiceRequestModal } from './OrderTaxInvoiceRequestModal';
import { OrderCanceldModal } from './ShopOrderCancelModal';

//* 면세계산서/수기환불 요청 상태 칩 색 — 라벨은 짧게 유지하고 색으로만 상태 구분
const requestChipClass = (status?: ShopOrderSellerRequestStatus) => {
  switch (status) {
    case 'Requested':
      return 'border-amber-500 bg-amber-50 text-amber-700';
    case 'Done':
      return 'border-green-600 bg-green-50 text-green-700';
    case 'Rejected':
      return 'border-red-500 bg-red-50 text-red-600';
    default:
      return 'border-gray-300 bg-white text-gray-700';
  }
};

interface Props {
  shopOrder: ShopOrderForSeller;
}

export const DeliveryStatusManageButton: FC<Props> = function DeliveryStatusManageButton({
  shopOrder,
}: Props) {
  const confirmOpenId = `deliveryStatusManageButton-${shopOrder._id}`;
  const deliveredOpenId = `deliveryStatusManageButton-delivered-${shopOrder._id}`;
  const cancelOpenId = `deliveryStatusManageButton-cancel-${shopOrder._id}`;
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(confirmOpenId));
  const setDeliveredOpen = useSetRecoilState(simpleOpenAtomFamily(deliveredOpenId));
  const setCancelOpen = useSetRecoilState(simpleOpenAtomFamily(cancelOpenId));

  //* 가상계좌·무통장(계좌 입금) 결제 — PG 자동취소가 안 되므로 취소 버튼이
  //* 일반 취소 모달 대신 수기환불(계좌이체) 요청으로 진입한다.
  const depositLike = isDepositLikePayment(shopOrder.paymentDetail);
  const taxInvoiceOpenId = `deliveryStatusManageButton-tax-invoice-${shopOrder._id}`;
  const setTaxInvoiceOpen = useSetRecoilState(simpleOpenAtomFamily(taxInvoiceOpenId));
  const manualRefundOpenId = `deliveryStatusManageButton-manual-refund-${shopOrder._id}`;
  const setManualRefundOpen = useSetRecoilState(simpleOpenAtomFamily(manualRefundOpenId));
  const manualRefundStatus = shopOrder.manualRefund?.status;
  const taxInvoiceStatus = shopOrder.taxInvoiceRequest?.status;
  const [updateStatus, setUpdateState] = useState(DeliveryState.BEFORE_PREPARING);
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();

  const onStatusUpdateClick = async (status: DeliveryState) => {
    const updateShopOrder_ = {
      _id: shopOrder._id,
      'deliveryDetail.status': status,
    };
    try {
      await updateShopOrder(updateShopOrder_, sellerRole());
      await queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
      notificateSuccess(notifiacationInstance, '상태변경에 성공하였습니다.');
    } catch (err: any) {
      notificateError(notifiacationInstance, '실패');
    }
  };

  const buttonContent = useMemo(() => {
    if (shopOrder.deliveryDetail.status === DeliveryState.BEFORE_PREPARING) {
      return (
        <Button
          className="button-sm border-none bg-cyan-600 px-2 text-white hover:bg-cyan-700"
          onClick={() => {
            setUpdateState(DeliveryState.PREPARING);
            setOpen(true);
          }}
        >
          상품 준비 시작
        </Button>
      );
    } else if (shopOrder.deliveryDetail.status === DeliveryState.PREPARING) {
      return (
        <Button
          className="button-sm border-none bg-cyan-600 px-2 text-white hover:bg-cyan-700"
          onClick={async () => {
            setUpdateState(DeliveryState.DELIVERYING);
            setOpen(true);
          }}
        >
          배송 시작
        </Button>
      );
    } else if (shopOrder.deliveryDetail.status === DeliveryState.DELIVERYING) {
      return (
        <Button
          className="button-sm border-none bg-green-500 px-2 text-white hover:bg-green-600"
          onClick={async () => {
            setDeliveredOpen(true);
          }}
        >
          배송 완료
        </Button>
      );
    } else if (shopOrder.deliveryDetail.status === DeliveryState.DELIVERED) {
      return (
        <button
          type="button"
          className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700 hover:bg-gray-100"
          onClick={() => setDeliveredOpen(true)}
        >
          <div className="text-center">
            <p>
              {formatDate(shopOrder.deliveryDetail.deliveredAt, {
                dateSeparater: '.',
                contains: {
                  year: false,
                  day: true,
                  hours: true,
                  minutes: true,
                },
              })}
            </p>
            <p>배송완료 됨 (시각수정)</p>
          </div>
        </button>
      );
    } else return null;
  }, [
    setDeliveredOpen,
    setOpen,
    shopOrder.deliveryDetail.deliveredAt,
    shopOrder.deliveryDetail.status,
  ]);

  const cancelButton = useMemo(() => {
    if (shopOrder.paymentDetail.status === PaymentState.CONFIRMED) {
      return (
        <div className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700">
          구매확정 완료됨
        </div>
      );
    } else if (
      [PaymentState.CANCELED, PaymentState.CANCEL_REQUESTED].includes(
        shopOrder.paymentDetail.status,
      )
    ) {
      //* 취소돼도 이체는 수동이므로 입금결제 건은 칩에서 수기환불 요청으로 진입 가능
      if (depositLike) {
        return (
          <button
            type="button"
            className={`button-sm center-box border px-2 ${requestChipClass(
              manualRefundStatus,
            )}`}
            onClick={() => setManualRefundOpen(true)}
          >
            {manualRefundStatus
              ? `환불 ${sellerRequestStatusLabel[manualRefundStatus]}`
              : shopOrder.paymentDetail.status === PaymentState.CANCELED
              ? '취소됨'
              : '취소 요청 중'}
          </button>
        );
      }
      return (
        <div className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700">
          {shopOrder.paymentDetail.status === PaymentState.CANCELED
            ? '취소됨'
            : '취소 요청 중'}
        </div>
      );
    } else if (depositLike) {
      //* 입금결제 건은 일반 취소(PG 자동환불)가 동작하지 않음 — 계좌환불신청으로 진입
      return (
        <Button
          className={
            manualRefundStatus
              ? `button-sm border px-2 ${requestChipClass(manualRefundStatus)}`
              : 'button-sm border-none bg-warn px-2 text-white hover:bg-warn'
          }
          onClick={async () => {
            setManualRefundOpen(true);
          }}
        >
          {manualRefundStatus
            ? `환불 ${sellerRequestStatusLabel[manualRefundStatus]}`
            : '취소(계좌)'}
        </Button>
      );
    } else {
      return (
        <Button
          className="button-sm border-none bg-warn px-6 text-white hover:bg-warn"
          onClick={async () => {
            setCancelOpen(true);
          }}
        >
          취소
        </Button>
      );
    }
  }, [
    depositLike,
    manualRefundStatus,
    setCancelOpen,
    setManualRefundOpen,
    shopOrder.paymentDetail.status,
  ]);

  //* 계산서요청 버튼 — 주문 반려(취소) 우측, 입금결제 + 결제완료(또는 기존 요청 존재) 시
  const taxInvoiceButton = useMemo(() => {
    if (!depositLike) return null;
    const eligible =
      [PaymentState.PAID, PaymentState.CONFIRMED].includes(
        shopOrder.paymentDetail.status,
      ) || !!taxInvoiceStatus;
    if (!eligible) return null;
    return (
      <button
        type="button"
        className={`button-sm center-box border px-2 ${requestChipClass(
          taxInvoiceStatus,
        )}`}
        onClick={() => setTaxInvoiceOpen(true)}
      >
        계산서요청
      </button>
    );
  }, [depositLike, taxInvoiceStatus, setTaxInvoiceOpen, shopOrder.paymentDetail.status]);

  return (
    <>
      <div className="flex w-full justify-between">
        <div className="flex gap-1.5">
          {cancelButton}
          {taxInvoiceButton}
        </div>
        {buttonContent}
      </div>
      <ConfirmModal
        openId={confirmOpenId}
        title={deliveryStateToString(updateStatus) + '로 변경'}
        buttonTitle={'상태 업데이트'}
        onClick={async () => {
          await onStatusUpdateClick(updateStatus);
        }}
      />
      <DeliveredModal openId={deliveredOpenId} shopOrder={shopOrder}></DeliveredModal>
      <OrderCanceldModal openId={cancelOpenId} shopOrder={shopOrder}></OrderCanceldModal>
      {depositLike && (
        <>
          <OrderTaxInvoiceRequestModal openId={taxInvoiceOpenId} order={shopOrder} />
          <OrderManualRefundRequestModal openId={manualRefundOpenId} order={shopOrder} />
        </>
      )}
    </>
  );
};
